/**
 * SEO helpers for React components (Helmet meta tags + JSON-LD).
 */
export const SITE_URL = 'https://www.merra.ai';
export const SITE_NAME = 'Merra AI';
export const TITLE_SUFFIX = ' | Merra AI';
export const MAX_TITLE_LEN = 60;
export const MAX_DESC_LEN = 155;

export function truncateAtWord(text, maxLen) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= maxLen) return clean;
  const slice = clean.slice(0, maxLen);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > maxLen * 0.6 ? slice.slice(0, lastSpace) : slice;
  return `${cut.trim()}…`;
}

export function formatPageTitle(title, { suffix = TITLE_SUFFIX, maxLen = MAX_TITLE_LEN } = {}) {
  const raw = String(title || SITE_NAME).trim();
  const hasBrand = /\bMerra(\.ai| AI)\s*$/i.test(raw);
  const full = hasBrand ? raw : `${raw}${suffix}`;
  if (full.length <= maxLen) return full;
  const effectiveSuffix = hasBrand ? '' : suffix;
  const maxBase = maxLen - effectiveSuffix.length - 1;
  return `${truncateAtWord(raw.replace(/\s*[-|–]\s*Merra(\.ai| AI)\s*$/i, '').trim() || raw, maxBase)}${effectiveSuffix || suffix}`;
}

export function formatDescription(text, maxLen = MAX_DESC_LEN) {
  return truncateAtWord(text, maxLen);
}

export function buildOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/web-app-manifest-512x512.png`,
    sameAs: ['https://www.linkedin.com/company/merra-hq'],
  };
}

export function buildWebSiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    publisher: { '@type': 'Organization', name: SITE_NAME },
  };
}

export function buildWebPageSchema({ name, description, url }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    description: formatDescription(description),
    url,
    isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: SITE_URL },
  };
}

export function buildSoftwareApplicationSchema({ name, description, url }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: name || SITE_NAME,
    description: formatDescription(description),
    url,
    applicationCategory: 'MultimediaApplication',
    operatingSystem: 'Web, iOS, Android',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    publisher: { '@type': 'Organization', name: SITE_NAME },
  };
}

export function buildArticleSchema({ title, description, url, datePublished }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: formatDescription(description),
    url,
    datePublished: datePublished || undefined,
    author: { '@type': 'Organization', name: SITE_NAME },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/web-app-manifest-512x512.png` },
    },
  };
}

export function buildSchemaGraph(schemas) {
  const items = schemas.filter(Boolean);
  if (items.length === 0) return null;
  if (items.length === 1) return items[0];
  return { '@context': 'https://schema.org', '@graph': items };
}
